import styles from './InvestigationAssets.module.scss';
import { ReactElement, useMemo } from 'react';
import {
  InvestigationAssetTrackCompleteness,
  InvestigationAssetTrackBasicModel,
  InvestigationAssetType,
} from '@customer/store/apis/investigationsApi';
import Skeleton from 'react-loading-skeleton';
import { NoResults } from '@dmc/ui';
import AssetCard from '@customer/components/data-display/asset-card/AssetCard';
import { useTranslation } from 'react-i18next';

// Prioritise Local, then Remote, then Nearby.
const sortOrder: Record<InvestigationAssetType, number> = {
  [InvestigationAssetType.Local]: 3,
  [InvestigationAssetType.Remote]: 2,
  [InvestigationAssetType.Nearby]: 1,
};

export const sortInvestigationAssets = <T extends { typ: InvestigationAssetType }>(
  a: T,
  b: T,
): number => {
  return sortOrder[a.typ] - sortOrder[b.typ];
};

interface InvestigationAssetsProps {
  assets?: InvestigationAssetTrackBasicModel[];
}

export const InvestigationAssets = ({ assets }: InvestigationAssetsProps): ReactElement => {
  const { t } = useTranslation();

  const isLoading = typeof assets === 'undefined';
  const hasAssets = !!assets?.length;

  const sortedAssets = useMemo<InvestigationAssetTrackBasicModel[]>(() => {
    // Reverse the order after sorting for UI display.
    return assets ? [...assets].sort(sortInvestigationAssets).reverse() : [];
  }, [assets]);

  return (
    <div className={styles.container}>
      {isLoading ? (
        <>
          {[...Array(3).keys()].map(key => (
            <Skeleton key={key} height={62} width={200} />
          ))}
        </>
      ) : hasAssets ? (
        <>
          {sortedAssets.map(
            ({
              asset: assetId,
              typ,
              n,
              cfgUpd,
              flts,
              cmp,
              obj,
              trkd,
            }: InvestigationAssetTrackBasicModel) => {
              const badStatus =
                cfgUpd === false ||
                flts.findIndex(f => f.lvl === 1) !== -1 ||
                cmp === InvestigationAssetTrackCompleteness.None ||
                cmp === InvestigationAssetTrackCompleteness.OneSecondFailed;

              return (
                <AssetCard
                  key={assetId}
                  assetId={assetId}
                  name={n}
                  type={typ}
                  badStatus={badStatus}
                  noData={!trkd}
                  objectType={obj}
                  cmp={cmp}
                />
              );
            },
          )}
        </>
      ) : (
        <NoResults description={t('NoAssets')} height={60} background="transparent" />
      )}
    </div>
  );
};

export default InvestigationAssets;
